// >>> THE BOOK READER, OUT OF THE SHELL (R8, paid by BREACH-1). <<<
//
// App.tsx's own note at the Library said the reason for holding these back — `ModalBackdrop` living
// in the shell — **was retired by R81 and nobody had gone back for them.** This is going back.
//
// It carries `useBookSearch`, whose `let active` sequencing law R216 audited and declared CORRECT
// (its read is started by the effect that owns the flag), and `ReaderState`, the four-state answer
// the reader draws from. Inside an eleven-thousand-line component neither could be imported, so
// neither had ever been reachable by a gate.
import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { ArrowLeft, ArrowRight, Search } from "lucide-react";
import { ModalBackdrop } from "./ModalBackdrop";
import { cleanBookParagraphs } from "../lib/library-text";
import { toReaderIndex, toBookmarkPage } from "../lib/bookmarks";
import { BookHitList } from "./BookHitList";
import BookmarkControls from "./BookmarkControls";
import { ReaderPending } from "./ReaderPending";
import { SaveToRoomButton } from "./SaveToRoomButton";
import { useSavedShelf } from "./useSavedShelf";
import { searchBooks, searchStateFrom, IDLE_SEARCH, type BookSearchState } from "../lib/library-search";
import { t } from "../strings.js";
export type ReaderState = {
  loading: boolean;
  hasText: boolean;
  needsAuth: boolean;
  pageCount: number;
  content: string;
};

// R3 — SEARCH THE BOOKS. One hook and one hit-list component, shared by the library-wide box and the
// reader's in-book box, so a member cannot get two different answers for the same words.
// Debounced so a member typing a word does not fire eight full-text queries, and race-safe so a slow
// early query can never overwrite the answer to what she has actually typed.
export function useBookSearch(term: string, slug = "") {
  // R93: the fabricated `{ hits: [], ran: true }` on every failure is gone — the transport in
  // src/lib/library-search.ts answers, and `searchStateFrom` is the ONE place an answer becomes the
  // next state, so the two search boxes cannot drift.
  const [state, setState] = useState<BookSearchState>(IDLE_SEARCH);
  useEffect(() => {
    const q = term.trim();
    if (q.length < 3) { setState(IDLE_SEARCH); return; }
    let active = true;
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(() => {
      void searchBooks(q, slug).then((answer) => { if (active) setState(searchStateFrom(answer)); });
    }, 280);
    return () => { active = false; clearTimeout(timer); };
  }, [term, slug]);
  return state;
}

export function BookReaderModal({
  book,
  page,
  setPage,
  onClose,
  language,
}: {
  book: { slug: string; title: string; author: string | null };
  page: number;
  setPage: (page: number) => void;
  onClose: () => void;
  language: string;
}) {
  const [state, setState] = useState<ReaderState>({
    loading: true, hasText: false, needsAuth: false, pageCount: 0, content: "",
  });
  const [fontRem, setFontRem] = useState(1.08);
  const [jump, setJump] = useState("");
  const [find, setFind] = useState("");           // R3: search inside THIS book
  const hits = useBookSearch(find, book.slug);
  // W5-2: the save was written here first and nowhere else, which is why her three books had none.
  // The route, the label and the optimistic toggle are shared with every other surface now.
  const shelf = useSavedShelf();

  // Fetch the full book text from the database, one page at a time (auth-gated).
  useEffect(() => {
    let active = true;
    setState((s) => ({ ...s, loading: true }));
    fetch(`/api/library/books/${encodeURIComponent(book.slug)}/pages?page=${page + 1}`)
      .then(async (r) => ({ status: r.status, data: await r.json().catch(() => ({})) }))
      .then(({ status, data }) => {
        if (!active) return;
        if (status === 401) {
          setState({ loading: false, hasText: false, needsAuth: true, pageCount: 0, content: "" });
        } else if (data && data.found && data.hasText) {
          setState({ loading: false, hasText: true, needsAuth: false, pageCount: data.pageCount, content: data.content });
        } else {
          setState({ loading: false, hasText: false, needsAuth: false, pageCount: 0, content: "" });
        }
      })
      .catch(() => {
        if (active) setState({ loading: false, hasText: false, needsAuth: false, pageCount: 0, content: "" });
      });
    return () => { active = false; };
  }, [book.slug, page]);

  const totalPages = state.hasText ? state.pageCount : 1;
  const paragraphs = state.hasText ? cleanBookParagraphs(state.content, book.title) : [];

  const submitJump = (event: FormEvent) => {
    event.preventDefault();
    const target = parseInt(jump, 10);
    if (Number.isFinite(target)) setPage(Math.max(0, Math.min(totalPages - 1, target - 1)));
    setJump("");
  };

  return (
    <ModalBackdrop label={t(language, "reader.modalLabel", { title: book.title })}>
      <article
        className="reader reader--modal"
        onCopy={(event) => event.preventDefault()}
        onCut={(event) => event.preventDefault()}
        onContextMenu={(event) => event.preventDefault()}
      >
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "reader.close")}>
          x
        </button>

        {/* Mid-page arrows: page through without scrolling to the bottom controls. */}
        {state.hasText && totalPages > 1 ? (
          <>
            <button className="reader__arrow reader__arrow--prev" type="button" disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))} aria-label={t(language, "reader.prevPage")}>
              <ArrowLeft size={22} />
            </button>
            <button className="reader__arrow reader__arrow--next" type="button" disabled={page >= totalPages - 1} onClick={() => setPage(Math.min(totalPages - 1, page + 1))} aria-label={t(language, "reader.nextPage")}>
              <ArrowRight size={22} />
            </button>
          </>
        ) : null}

        {/* Title + author appear once, when the book opens. */}
        <header className="reader__head">
          <h2 className="reader__title">{book.title}</h2>
          {book.author ? <p className="reader__author">{book.author}</p> : null}
          <SaveToRoomButton slug={book.slug} title={book.title} saved={shelf.isSaved(book.slug)} onToggle={shelf.toggle} language={language} />
        </header>

        {state.hasText ? (
          <div className="reader__size" role="group" aria-label={t(language, "reader.textSize")}>
            <button type="button" onClick={() => setFontRem((f) => Math.max(0.85, +(f - 0.1).toFixed(2)))} aria-label={t(language, "reader.smaller")}>
              A−
            </button>
            <span>{t(language, "reader.textSize")}</span>
            <button type="button" onClick={() => setFontRem((f) => Math.min(1.6, +(f + 0.1).toFixed(2)))} aria-label={t(language, "reader.larger")}>
              A+
            </button>
          </div>
        ) : null}

        {/* R3 — SEARCH INSIDE THIS BOOK. A hit is a page number, so finding a passage and turning to
            it is one tap. Clearing the box puts the reader back where it was, untouched. */}
        {state.hasText ? (
          <div className="reader__search">
            <label className="library-search library-search--reader">
              <Search size={15} className="library-search__icon" />
              <input
                type="search"
                value={find}
                onChange={(e) => setFind(e.target.value)}
                placeholder={t(language, "reader.searchInside", { book: book.title.length > 28 ? t(language, "reader.thisBook") : book.title })}
                aria-label={t(language, "reader.searchInsideLabel", { book: book.title })}
              />
            </label>
            <BookHitList
              state={hits}
              term={find}
              showTitles={false}
              language={language}
              onOpen={(hit) => { setPage(Math.max(0, hit.page - 1)); setFind(""); }}
            />
          </div>
        ) : null}

        {state.needsAuth ? (
          <p className="reader__page reader__page--note">{t(language, "reader.signIn")}</p>
        ) : state.loading ? (
          <p className="reader__page reader__page--note">{t(language, "reader.opening")}</p>
        ) : state.hasText ? (
          <div className="reader__page reader__page--book" style={{ ["--reader-font" as string]: `${fontRem}rem` } as React.CSSProperties}>
            {paragraphs.map((para, index) => (
              <p key={index}>{para}</p>
            ))}
          </div>
        ) : (
          <ReaderPending title={book.title} language={language} />
        )}

        {state.hasText ? (
          <div className="reader__controls">
            <button type="button" disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))}>
              {t(language, "reader.prev")}
            </button>
            <span className="reader__pageno">{t(language, "reader.pageOf", { page: page + 1, total: totalPages })}</span>
            <button type="button" disabled={page >= totalPages - 1} onClick={() => setPage(Math.min(totalPages - 1, page + 1))}>
              {t(language, "reader.next")}
            </button>
            <form className="reader__jump" onSubmit={submitJump}>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={jump}
                onChange={(event) => setJump(event.target.value)}
                placeholder={t(language, "reader.jumpPlaceholder")}
                aria-label={t(language, "reader.jumpLabel")}
              />
              <button type="submit">{t(language, "reader.go")}</button>
            </form>
          </div>
        ) : null}
        {/* W1-6 root cause: the bar was mounted behind `state.hasText`, so a book with no ingested
            text showed none. Her law is every book, so only being signed out holds it back. */}
        {state.needsAuth ? null : (
          <BookmarkControls
            bookSlug={book.slug}
            bookTitle={book.title}
            page={toBookmarkPage(page)}
            onJump={(p) => setPage(toReaderIndex(p, totalPages))}
            language={language}
          />
        )}
      </article>
    </ModalBackdrop>
  );
}
